import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import styles from './AgencyForm.module.scss';
import {
  AgencyFormProps,
  AgencyFormValues,
  AgencyStatus,
  CreateAgencyRequest,
  defaultValues,
  UpdateAgencyRequest,
  VALIDATION_MESSAGE
} from '../../interfaces/types';
import { Typography } from '@navi/web-ui/lib/primitives';
import AgencyFormTextbox from './AgencyFormTextbox';
import { validateEmail } from '@cp/src/pages/AllAgents/AgentForm/utils/utils';
import RequiredIcon from '@navi/web-ui/lib/icons/RequiredIcon';
import {
  createNewAgency,
  fetchStatesData,
  getAgencyTypes,
  getAllAgencies,
  updateAgency
} from '../../actions/agencyActions';
import AgencyFormChip from './AgencyFormChip';
import FormToggleTab from './FormToggleTab';
import AgencyFormStatesDropdown from './AgencyFormMultipleSelectDropdown';
import AgencyFormRadioInput from './AgencyFormRadioInput';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { AGENCY_STATUS_OPTIONS, AGENCY_TEAM_OPTIONS, tabOptions } from '../../constants/constants';
import { isFunction } from '@tanstack/react-table';
import LoaderIcon from '@cp/src/assets/icons/LoaderIcon';
import { setIsTerminateAgencyOptionSelected } from '@cp/src/reducers/AgencyDetailsSlice';

export const AgencyForm = forwardRef((props: AgencyFormProps, ref) => {
  const {
    isDrawerOpen,
    onClose,
    isEditDisabled,
    setIsSubmitButtonDisabled,
    isEditAgencyPage = false,
    setIsDirtyField
  } = props;
  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty, errors },
    watch,
    setValue
  } = useForm<AgencyFormValues>({
    defaultValues,
    mode: 'onTouched',
    reValidateMode: 'onBlur'
  });
  const [isDisabled, setIsDisabled] = useState(false);
  const dispatch = useDispatch();
  const allAgencyPage = useSelector((state: RootState) => state.agencyDashboard?.allAgencyPage);
  const allStates = useSelector((state: RootState) => state.agencyDashboard?.states);
  const agency = useSelector((state: RootState) => state.agencyDashboard?.agencyData);
  const showLoader = useSelector((state: RootState) => state.agencyDashboard?.showLoader);
  const agencyTypesOptions = useSelector(
    (state: RootState) => state.agencyDashboard?.agencyTypesData
  );

  const agencyTypeSubTypeMap = useSelector(
    (state: RootState) => state.agencyDashboard?.agencyTypeMappings
  );

  useEffect(() => {
    if (agency?.referenceId && isEditAgencyPage) {
      const agencyTypes = Object.keys(agency?.agencyTypes || {});
      reset({
        agencyCode: agency?.code,
        agencyName: agency?.name,
        agencyTeam: agency?.agencyTeam || '',
        agencyTypes: agencyTypes,
        oracleId: agency?.oracleId,
        status: agency?.status,
        serviceableStates: agency?.serviceableStates,
        nextMonthAvailability:
          agency?.nextMonthAvailability && agency?.status === AgencyStatus.ACTIVE,
        oneManAgency: agency?.oneManAgency
      });
    } else {
      reset(defaultValues);
    }
  }, [agency]);

  useEffect(() => {
    if (!isDrawerOpen && !isEditAgencyPage) reset(defaultValues);
  }, [isDrawerOpen]);

  const refreshAgencyList = () => {
    dispatch(
      getAllAgencies({
        pageSize: allAgencyPage?.pageSize?.toString() || '20',
        pageIndex: allAgencyPage?.pageNo?.toString() || '0'
      })
    );
    onClose();
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(createOrUpdateAgency)();
    }
  }));

  const name = watch('agencyName');
  const code = watch('agencyCode');
  const typeOfAgency = watch('agencyTypes');
  const team = watch('agencyTeam');
  const teamLeadName = watch('managerName');
  const teamLeadEmail = watch('managerEmailAddress');
  const teamLeadPhone = watch('managerPhoneNumber');
  const agencyCurrentStatus = watch('status');

  useEffect(() => {
    const isCreateFormValid = !!(
      name &&
      code &&
      typeOfAgency?.length &&
      team &&
      teamLeadName &&
      teamLeadEmail &&
      teamLeadPhone
    );
    const isUpdateFormValid = !!(name && code && typeOfAgency?.length && team);
    isEditAgencyPage
      ? setIsSubmitButtonDisabled(!isUpdateFormValid)
      : setIsSubmitButtonDisabled(!isCreateFormValid);
  }, [
    name,
    code,
    typeOfAgency,
    team,
    teamLeadName,
    teamLeadEmail,
    teamLeadPhone,
    isEditAgencyPage,
    setIsSubmitButtonDisabled
  ]);

  useEffect(() => {
    if (isFunction(setIsDirtyField)) {
      setIsDirtyField(isDirty);
    }
  }, [isDirty]);

  useEffect(() => {
    dispatch(fetchStatesData);
    dispatch(getAgencyTypes);
  }, []);

  const createOrUpdateAgency = (data: AgencyFormValues) => {
    const agencyTypes = data?.agencyTypes;
    const agencyTypePayload: { agencyTypeReferenceId: string | null }[] = [];
    agencyTypes?.forEach(type => {
      agencyTypePayload.push({
        agencyTypeReferenceId: agencyTypeSubTypeMap[type]?.referenceId ?? null
      });
    });
    const updatedPayload = {
      ...data,
      agencyTypeSubTypeMappings: agencyTypePayload
    };
    delete updatedPayload?.agencyTypes;

    if (agency?.referenceId) {
      dispatch(
        updateAgency(
          agency?.referenceId as string,
          updatedPayload as UpdateAgencyRequest,
          setIsSubmitButtonDisabled,
          refreshAgencyList
        )
      );
    } else {
      dispatch(
        createNewAgency(
          updatedPayload as CreateAgencyRequest,
          setIsSubmitButtonDisabled,
          refreshAgencyList
        )
      );
    }
  };

  useEffect(() => {
    if (
      agencyCurrentStatus === AgencyStatus.INACTIVE ||
      agencyCurrentStatus === AgencyStatus.TERMINATED
    ) {
      setValue('nextMonthAvailability', false);
      setIsDisabled(true);
    }
    if (agencyCurrentStatus === AgencyStatus.ACTIVE) {
      setIsDisabled(false);
    }
  }, [agencyCurrentStatus, isDisabled]);

  useEffect(() => {
    if (agencyCurrentStatus === AgencyStatus.TERMINATED) {
      dispatch(setIsTerminateAgencyOptionSelected(true));
    } else dispatch(setIsTerminateAgencyOptionSelected(false));
  }, [agencyCurrentStatus]);

  if (showLoader) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoaderIcon
          size={40}
          borderWidth={4}
          borderColor="var(--navi-color-gray-border)"
          fillColor="var(--navi-color-blue-base)"
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(createOrUpdateAgency)}>
      <div className={styles.activeContainer}>
        <div>
          <div className={'pt-5 pl-6'}>
            {isEditAgencyPage ? (
              <>
                <div className="mb-6">
                  <Typography
                    variant="h3"
                    color={'var(--grayscale-1)'}
                    className="max-w-[300px] text-ellipsis whitespace-nowrap  overflow-hidden"
                  >
                    {agency?.name}
                  </Typography>
                  <Typography variant="p3" color={'var(--grayscale-content-3'}>
                    {agency?.code}
                  </Typography>
                </div>
              </>
            ) : null}
            <Typography variant={'h4'} color="var(--navi-color-gray-c2)">
              Agency Details
            </Typography>
          </div>
          {!isEditAgencyPage && (
            <>
              <div className={styles.inputContainer}>
                <AgencyFormTextbox
                  control={control}
                  name="agencyName"
                  rules={{ required: true }}
                  errors={errors}
                  isEditDisabled={isEditDisabled || isEditAgencyPage}
                  styles={styles}
                  inputLabel={'Name'}
                  placeholder={'Enter here'}
                  validationMessage={VALIDATION_MESSAGE.AGENCY_NAME_MESSAGE}
                />
              </div>
              <div className={styles.inputContainer}>
                <AgencyFormTextbox
                  control={control}
                  name="agencyCode"
                  rules={{ required: true }}
                  errors={errors}
                  isEditDisabled={isEditDisabled || isEditAgencyPage}
                  styles={styles}
                  inputLabel={'Agency Code'}
                  placeholder={'Enter a unique code'}
                  validationMessage={VALIDATION_MESSAGE.AGENCY_CODE_MESSAGE}
                  isRequired={true}
                  isUpperCaseRequired={true}
                />
              </div>
              <div className={styles.inputContainer}>
                <AgencyFormTextbox
                  control={control}
                  name="managerName"
                  rules={{ required: true }}
                  errors={errors}
                  isEditDisabled={isEditDisabled || isEditAgencyPage}
                  styles={styles}
                  inputLabel={'Team Lead Name'}
                  placeholder={'Enter here'}
                  validationMessage={VALIDATION_MESSAGE.TL_NAME_MESSAGE}
                  isRequired={true}
                  isUpperCaseRequired={false}
                />
              </div>
              <div className={styles.inputContainer}>
                <AgencyFormTextbox
                  control={control}
                  name="managerEmailAddress"
                  rules={{
                    required: true,
                    validate: (value: string) => {
                      if (watch('managerEmailAddress')) {
                        return validateEmail(value, false);
                      }
                    }
                  }}
                  errors={errors}
                  isEditDisabled={isEditDisabled || isEditAgencyPage}
                  styles={styles}
                  inputLabel={'Team Lead Email Address'}
                  placeholder={'Enter here'}
                  validationMessage={VALIDATION_MESSAGE.TL_EMAIL_MESSAGE}
                  isRequired={true}
                />
              </div>
              <div className={styles.inputContainer}>
                <AgencyFormTextbox
                  control={control}
                  name="managerPhoneNumber"
                  rules={{
                    required: true,
                    minLength: 10,
                    maxLength: 10,
                    pattern: /^[0-9]{10}$/i
                  }}
                  errors={errors}
                  isEditDisabled={isEditDisabled || isEditAgencyPage}
                  styles={styles}
                  inputLabel={'Team Lead Phone Number'}
                  placeholder={'Enter here'}
                  validationMessage={VALIDATION_MESSAGE.TL_PHONE_NUMBER_MESSAGE}
                  isRequired={true}
                />
              </div>
            </>
          )}
          <div className={styles.inputContainer}>
            <AgencyFormTextbox
              control={control}
              name="oracleId"
              rules={{ required: false }}
              errors={errors}
              isEditDisabled={isEditDisabled}
              styles={styles}
              inputLabel={'Oracle ID'}
              placeholder={'Enter here'}
              isRequired={false}
              validationMessage={''}
            />
          </div>
          <div className={styles.inputContainer}>
            <AgencyFormStatesDropdown
              control={control}
              name={'serviceableStates'}
              rules={{ required: false }}
              errors={errors}
              isEditDisabled={isEditDisabled}
              styles={styles}
              validationMessage={''}
              options={allStates}
            />
          </div>
          <div className={styles.inputContainer}>
            <AgencyFormChip
              control={control}
              name={'agencyTypes'}
              rules={{ required: true }}
              errors={errors}
              styles={styles}
              inputLabel={'Purpose'}
              validationMessage={'This field is required'}
              inputOptions={agencyTypesOptions}
              isRequired={true}
              isMultipleSelectionAllowed={true}
              isEditDisabled={isEditDisabled}
            />
          </div>
          <div className={styles.inputContainer}>
            <AgencyFormChip
              control={control}
              name={'agencyTeam'}
              rules={{ required: true }}
              errors={errors}
              styles={styles}
              inputLabel={'Agency Team (Based on DPD Bucket)'}
              validationMessage={'This field is required'}
              inputOptions={AGENCY_TEAM_OPTIONS}
              isEditDisabled={isEditDisabled}
            />
          </div>
          <div className={styles.inputContainer}>
            <div className="flex flex-row justify-between items-center pr-8">
              <div className="flex flex-row gap-1">
                <Typography variant="p4" className="text-grayscale-2">
                  OMA (One Man Agency)
                </Typography>
                <RequiredIcon />
              </div>
              <FormToggleTab
                control={control}
                name="oneManAgency"
                errors={errors}
                rules={{ required: false }}
                styles={styles}
                validationMessage={'This field is required'}
                tabOptions={tabOptions}
              />
            </div>
          </div>
          {isEditAgencyPage && (
            <>
              <div className={styles.inputContainer}>
                <AgencyFormRadioInput
                  control={control}
                  name="status"
                  rules={{ required: false }}
                  errors={errors}
                  isEditDisabled={false}
                  styles={styles}
                  inputLabel={'Agency status'}
                  isRequired={false}
                  options={AGENCY_STATUS_OPTIONS}
                  validationMessage={''}
                />
              </div>
              <div className={styles.inputContainer}>
                <div className="flex flex-row justify-between items-center pr-8">
                  <div className="flex flex-row gap-1">
                    <Typography variant="p4" className="text-grayscale-2">
                      Available for next month allocation
                    </Typography>
                  </div>
                  <Tooltip placement="top">
                    {isDisabled ? (
                      <TooltipContent className="tooltipWrapper">
                        Please mark agency as active to change this
                      </TooltipContent>
                    ) : null}
                    <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
                      <FormToggleTab
                        control={control}
                        name="nextMonthAvailability"
                        errors={errors}
                        styles={styles}
                        validationMessage={'This field is required'}
                        tabOptions={tabOptions}
                        isToggleDisabled={isDisabled}
                      />
                    </TooltipTrigger>
                  </Tooltip>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </form>
  );
});
